import React, { useMemo, useRef, useCallback, useEffect, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { AgendaGroup, AgendaJobData } from "../agenda.types";
import { useAgendaStore, GroupMode } from "../agenda.store";
import { ClientSection, FlatJobRow } from "./collapsible-list.component";
import {
	formatDate,
	formatHour,
	getAllDaysInRange,
	getAllHoursInRange,
	getAllWeeksInRange,
	isSameDay,
	isWeekend,
	DAY_COL_WIDTH,
	WEEK_COL_WIDTH,
	HOUR_COL_WIDTH,
	WeekRange,
} from "./utils";
import clsx from "clsx";

const MIN_SIDEBAR_WIDTH = 120;
const MAX_SIDEBAR_WIDTH = 500;

interface GanttChartProps {
	groups: AgendaGroup[];
	expandedGroupIds: string[];
	onToggleGroup: (groupId: string) => void;
	dateRange: { start: string; end: string };
	viewMode: "day" | "week" | "hour";
	hourViewDate?: string;
	fullScreen?: boolean;
	scrollToTodayTrigger?: number;
	onJobNavigate: (groupId: string, jobId: number) => void;
	onJobClick: (job: AgendaJobData, groupId: string) => void;
	onExpandAll?: () => void;
	onCollapseAll?: () => void;
	groupMode?: GroupMode;
	rowHeight?: number;
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
	groupLabel?: string;
}

export const GanttChart: React.FC<GanttChartProps> = ({
	groups,
	expandedGroupIds,
	onToggleGroup,
	dateRange,
	viewMode,
	hourViewDate,
	fullScreen,
	scrollToTodayTrigger,
	onJobNavigate,
	onJobClick,
	onExpandAll,
	onCollapseAll,
	groupMode = "client",
	rowHeight = 20,
	profileColors,
	profilesMap,
	groupLabel = "Kunden",
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [timeNow, setTimeNow] = useState(() => new Date());
	const [containerWidth, setContainerWidth] = useState(0);
	const todayStr = useMemo(() => timeNow.toISOString().split("T")[0], [timeNow]);
	const sidebarWidth = useAgendaStore((s) => s.sidebarWidth);
	const setSidebarWidth = useAgendaStore((s) => s.setSidebarWidth);
	const resizeRef = useRef({ startX: 0, startWidth: 0 });

	useEffect(() => {
		const interval = setInterval(() => setTimeNow(new Date()), 30_000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const ro = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setContainerWidth(entry.contentRect.width);
			}
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const rangeStart = useMemo(
		() => new Date(dateRange.start),
		[dateRange.start]
	);
	const rangeEnd = useMemo(() => new Date(dateRange.end), [dateRange.end]);

	const daysInRange = useMemo(
		() => getAllDaysInRange(rangeStart, rangeEnd),
		[rangeStart, rangeEnd]
	);

	const weeksInRange = useMemo(
		() => getAllWeeksInRange(rangeStart, rangeEnd),
		[rangeStart, rangeEnd]
	);

	const hoursInRange = useMemo(() => getAllHoursInRange(), []);

	const availableWidth = Math.max(0, containerWidth - sidebarWidth);
	const colCount = viewMode === "hour" ? hoursInRange.length : viewMode === "day" ? daysInRange.length : weeksInRange.length;
	const minColWidth = viewMode === "hour" ? HOUR_COL_WIDTH : viewMode === "day" ? DAY_COL_WIDTH : WEEK_COL_WIDTH;
	const effectiveColWidth = colCount > 0 && availableWidth > 0
		? Math.max(minColWidth, Math.floor(availableWidth / colCount))
		: minColWidth;
	const timelineWidth = colCount * effectiveColWidth;

	const scrollToToday = useCallback(() => {
		if (!scrollRef.current) return;

		const visibleTimeline = scrollRef.current.clientWidth - sidebarWidth;

		if (viewMode === "hour") {
			const now = new Date();
			const currentHour = now.getHours();
			const hourIdx = hoursInRange.indexOf(currentHour);
			const targetIdx = hourIdx >= 0 ? hourIdx : 0;
			const targetScroll =
				targetIdx * effectiveColWidth -
				visibleTimeline / 2;
			scrollRef.current.scrollLeft = Math.max(0, targetScroll);
			return;
		}

		const msPerDay = 86400000;
		const startMs = new Date(rangeStart.toDateString()).getTime();
		const nowMs = new Date(new Date().toDateString()).getTime();
		const daysDiff = (nowMs - startMs) / msPerDay;

		if (viewMode === "day") {
			const targetScroll =
				daysDiff * effectiveColWidth -
				visibleTimeline / 2;
			scrollRef.current.scrollLeft = Math.max(0, targetScroll);
		} else {
			const pixelsPerDay = effectiveColWidth / 7;
			const targetScroll =
				daysDiff * pixelsPerDay -
				visibleTimeline / 2;
			scrollRef.current.scrollLeft = Math.max(0, targetScroll);
		}
	}, [rangeStart, viewMode, hoursInRange, sidebarWidth, effectiveColWidth]);

	const scrollToTodayRef = useRef(scrollToToday);
	scrollToTodayRef.current = scrollToToday;

	useEffect(() => {
		const timer = setTimeout(() => scrollToTodayRef.current(), 100);
		return () => clearTimeout(timer);
	}, [viewMode, scrollToTodayTrigger]);

	const handleResizeMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			resizeRef.current = { startX: e.clientX, startWidth: sidebarWidth };
			document.body.style.userSelect = "none";

			const handleMouseMove = (ev: MouseEvent) => {
				const delta = ev.clientX - resizeRef.current.startX;
				const newWidth = Math.min(
					MAX_SIDEBAR_WIDTH,
					Math.max(MIN_SIDEBAR_WIDTH, resizeRef.current.startWidth + delta)
				);
				setSidebarWidth(newWidth);
			};

			const handleMouseUp = () => {
				document.body.style.userSelect = "";
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		},
		[sidebarWidth, setSidebarWidth]
	);

	const todayIndicatorLeft = useMemo(() => {
		if (viewMode === "hour") {
			if (hourViewDate !== todayStr) return null;

			const currentHour = timeNow.getHours() + timeNow.getMinutes() / 60;
			const firstHour = hoursInRange[0];
			const lastHour = hoursInRange[hoursInRange.length - 1];
			if (currentHour < firstHour || currentHour > lastHour + 1) return null;

			const hourOffset = currentHour - firstHour;
			return sidebarWidth + hourOffset * effectiveColWidth;
		}

		const msPerDay = 86400000;
		const todayMs = new Date(timeNow.toDateString()).getTime();

		if (viewMode === "day") {
			const startMs = new Date(rangeStart.toDateString()).getTime();
			const daysDiff = (todayMs - startMs) / msPerDay;
			if (daysDiff < 0 || daysDiff > daysInRange.length) return null;
			return sidebarWidth + daysDiff * effectiveColWidth + effectiveColWidth / 2;
		}

		if (weeksInRange.length === 0) return null;
		const weekStartMs = new Date(weeksInRange[0].start.toDateString()).getTime();
		const daysDiff = (todayMs - weekStartMs) / msPerDay;
		if (daysDiff < 0 || daysDiff > weeksInRange.length * 7) return null;
		const pixelsPerDay = effectiveColWidth / 7;
		return sidebarWidth + daysDiff * pixelsPerDay + pixelsPerDay / 2;
	}, [rangeStart, timeNow, todayStr, daysInRange.length, viewMode, sidebarWidth, hoursInRange, hourViewDate, weeksInRange, effectiveColWidth]);

	return (
		<div
			className={clsx(
				"rounded-lg border border-gray-200 flex flex-col w-full h-full shadow-sm overflow-hidden",
				{ "rounded-none border-0": fullScreen }
			)}
		>
			<div
				ref={scrollRef}
				className="overflow-auto flex-1 relative"
				data-testid="agenda-scroll-container"
			>
				<div
					style={{
						minWidth: sidebarWidth + timelineWidth,
					}}
				>
					{viewMode === "hour" ? (
						<HourHeaders
							hoursInRange={hoursInRange}
							sidebarWidth={sidebarWidth}
							colWidth={effectiveColWidth}
							onResizeMouseDown={handleResizeMouseDown}
							onExpandAll={groupMode !== "flat" ? onExpandAll : undefined}
							onCollapseAll={groupMode !== "flat" ? onCollapseAll : undefined}
							groupLabel={groupLabel}
						/>
					) : viewMode === "day" ? (
						<DayHeaders
							daysInRange={daysInRange}
							today={timeNow}
							rangeStart={rangeStart}
							rangeEnd={rangeEnd}
							sidebarWidth={sidebarWidth}
							colWidth={effectiveColWidth}
							onResizeMouseDown={handleResizeMouseDown}
							onExpandAll={groupMode !== "flat" ? onExpandAll : undefined}
							onCollapseAll={groupMode !== "flat" ? onCollapseAll : undefined}
							groupLabel={groupLabel}
						/>
					) : (
						<WeekHeaders
							weeksInRange={weeksInRange}
							today={timeNow}
							sidebarWidth={sidebarWidth}
							colWidth={effectiveColWidth}
							onResizeMouseDown={handleResizeMouseDown}
							onExpandAll={groupMode !== "flat" ? onExpandAll : undefined}
							onCollapseAll={groupMode !== "flat" ? onCollapseAll : undefined}
							groupLabel={groupLabel}
						/>
					)}

					{groupMode === "flat" ? (
						(() => {
							let rowIdx = 0;
							return groups.flatMap((group) =>
								group.jobs.map((job) => {
									const even = rowIdx++ % 2 === 0;
									return (
										<FlatJobRow
											key={job.id}
											job={job}
											groupId={group.id}
											groupName={group.name}
											even={even}
											daysInRange={daysInRange}
											viewMode={viewMode}
											weeksInRange={weeksInRange}
											hoursInRange={hoursInRange}
											timelineWidth={timelineWidth}
											sidebarWidth={sidebarWidth}
											hourViewDate={hourViewDate}
											onJobNavigate={onJobNavigate}
											onJobClick={onJobClick}
											colWidth={effectiveColWidth}
											rowHeight={rowHeight}
											profileColors={profileColors}
											profilesMap={profilesMap}
										/>
									);
								})
							);
						})()
					) : (
						groups.map((group, i) => (
							<ClientSection
								key={group.id}
								group={group}
								expanded={expandedGroupIds.includes(group.id)}
								onToggle={onToggleGroup}
								even={i % 2 === 0}
								daysInRange={daysInRange}
								viewMode={viewMode}
								weeksInRange={weeksInRange}
								hoursInRange={hoursInRange}
								timelineWidth={timelineWidth}
								sidebarWidth={sidebarWidth}
								hourViewDate={hourViewDate}
								onJobNavigate={onJobNavigate}
								onJobClick={onJobClick}
								colWidth={effectiveColWidth}
								rowHeight={rowHeight}
								profileColors={profileColors}
								profilesMap={profilesMap}
							/>
						))
					)}
				</div>

				{todayIndicatorLeft !== null && (
					<div
						className="absolute top-10 bottom-0 border-l-2 border-dashed border-blue-400 pointer-events-none z-[5]"
						style={{ left: todayIndicatorLeft }}
					/>
				)}
			</div>
		</div>
	);
};

const SidebarHeaderCell: React.FC<{
	sidebarWidth: number;
	onResizeMouseDown: (e: React.MouseEvent) => void;
	onExpandAll?: () => void;
	label?: string;
	onCollapseAll?: () => void;
}> = ({ sidebarWidth, onResizeMouseDown, onExpandAll, onCollapseAll, label = "Kunden" }) => (
	<div
		className="sticky left-0 z-30 bg-gray-50 border-b border-r border-gray-200 shrink-0 flex items-center justify-between px-3 gap-1 text-xs text-gray-500 font-medium relative"
		style={{ width: sidebarWidth, minWidth: sidebarWidth }}
	>
		{onExpandAll ? label : 'Aufträge'}
		<div className="flex items-center gap-2">
		{onExpandAll && (
			<button
				onClick={onExpandAll}
				className="inline-flex items-center justify-center rounded hover:bg-gray-200 p-0.5"
				aria-label="Alle Gruppen aufklappen"
				data-testid="agenda-expand-all"
			>
				<ChevronsUpDown size={14} />
			</button>
		)}
		{onCollapseAll && (
			<button
				onClick={onCollapseAll}
				className="inline-flex items-center justify-center rounded hover:bg-gray-200 p-0.5"
				aria-label="Alle Gruppen zuklappen"
				data-testid="agenda-collapse-all"
			>
				<ChevronsDownUp size={14} />
			</button>
		)}
		</div>
		<div
			className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-400/50"
			onMouseDown={onResizeMouseDown}
			aria-label="Resize sidebar"
			data-testid="agenda-sidebar-resize-handle"
		/>
	</div>
);

const DayHeaders: React.FC<{
	daysInRange: Date[];
	today: Date;
	rangeStart: Date;
	rangeEnd: Date;
	sidebarWidth: number;
	colWidth: number;
	onResizeMouseDown: (e: React.MouseEvent) => void;
	onExpandAll?: () => void;
	onCollapseAll?: () => void;
	groupLabel?: string;
}> = React.memo(({ daysInRange, today, rangeStart, rangeEnd, sidebarWidth, colWidth, onResizeMouseDown, onExpandAll, onCollapseAll, groupLabel }) => {
	const showYear = rangeStart.getFullYear() !== rangeEnd.getFullYear();

	return (
		<div className="flex sticky top-0 z-20">
			<SidebarHeaderCell
				sidebarWidth={sidebarWidth}
				onResizeMouseDown={onResizeMouseDown}
				onExpandAll={onExpandAll}
				onCollapseAll={onCollapseAll}
				label={groupLabel}
			/>
			<div className="flex">
				{daysInRange.map((date) => {
					const isToday = isSameDay(date, today);
					const weekend = isWeekend(date);

					return (
						<div
							key={date.toISOString()}
							className={clsx(
								"h-10 flex items-center justify-center text-xs text-gray-600 border-b border-gray-200 shrink-0",
								{
									"bg-blue-50 font-medium text-blue-700":
										isToday,
									"bg-gray-100/50": weekend && !isToday,
									"bg-gray-50": !weekend && !isToday,
								}
							)}
							style={{ width: colWidth }}
						>
							<span className="flex flex-col items-center leading-tight">
								{formatDate(date)}
								{showYear && (
									<sup className="text-[9px] text-gray-400">
										{date.getFullYear()}
									</sup>
								)}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
});

const HourHeaders: React.FC<{
	hoursInRange: number[];
	sidebarWidth: number;
	colWidth: number;
	onResizeMouseDown: (e: React.MouseEvent) => void;
	onExpandAll?: () => void;
	onCollapseAll?: () => void;
	groupLabel?: string;
}> = React.memo(({ hoursInRange, sidebarWidth, colWidth, onResizeMouseDown, onExpandAll, onCollapseAll, groupLabel }) => {
	const now = new Date();
	const currentHour = now.getHours();

	return (
		<div className="flex sticky top-0 z-20">
			<SidebarHeaderCell
				sidebarWidth={sidebarWidth}
				onResizeMouseDown={onResizeMouseDown}
				onExpandAll={onExpandAll}
				onCollapseAll={onCollapseAll}
				label={groupLabel}
			/>
			<div className="flex">
				{hoursInRange.map((hour) => (
					<div
						key={hour}
						className={clsx(
							"h-10 flex items-center text-xs border-b border-gray-200 shrink-0 relative pl-1",
							{
								"bg-blue-50 font-medium text-blue-700": hour === currentHour,
								"bg-gray-50": hour !== currentHour,
							}
						)}
						style={{ width: colWidth }}
					>
						{formatHour(hour)}
						<span className="absolute bottom-0 left-0 w-px h-4 bg-gray-400" />
						<span className="absolute bottom-0 left-1/4 w-px h-1.5 bg-gray-300" />
						<span className="absolute bottom-0 left-1/2 w-px h-2.5 bg-gray-300" />
						<span className="absolute bottom-0 left-3/4 w-px h-1.5 bg-gray-300" />
					</div>
				))}
			</div>
		</div>
	);
});

const WeekHeaders: React.FC<{
	weeksInRange: WeekRange[];
	today: Date;
	sidebarWidth: number;
	colWidth: number;
	onResizeMouseDown: (e: React.MouseEvent) => void;
	onExpandAll?: () => void;
	onCollapseAll?: () => void;
	groupLabel?: string;
}> = React.memo(({ weeksInRange, today, sidebarWidth, colWidth, onResizeMouseDown, onExpandAll, onCollapseAll, groupLabel }) => {
	return (
		<div className="flex sticky top-0 z-20">
			<SidebarHeaderCell
				sidebarWidth={sidebarWidth}
				onResizeMouseDown={onResizeMouseDown}
				onExpandAll={onExpandAll}
				onCollapseAll={onCollapseAll}
				label={groupLabel}
			/>
			<div className="flex">
				{weeksInRange.map((week) => {
					const todayInWeek =
						today >= week.start && today <= week.end;
					const startStr = `${week.start
						.getDate()
						.toString()
						.padStart(2, "0")}.${(week.start.getMonth() + 1)
						.toString()
						.padStart(2, "0")}`;
					const endStr = `${week.end
						.getDate()
						.toString()
						.padStart(2, "0")}.${(week.end.getMonth() + 1)
						.toString()
						.padStart(2, "0")}`;

					return (
						<div
							key={`${week.year}-${week.weekNumber}`}
							className={clsx(
								"h-10 flex flex-col items-center justify-center text-xs border-b border-gray-200 shrink-0",
								{
									"bg-blue-50 text-blue-700 font-medium":
										todayInWeek,
									"bg-gray-50": !todayInWeek,
								}
							)}
							style={{ width: colWidth }}
						>
							<span className="font-medium">
								KW {week.weekNumber}
							</span>
							<span className="text-[10px] text-gray-400">
								{startStr} - {endStr}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
});
